import React, { useState, useContext, useEffect } from 'react';
import {
  Row, Col, Form, Input, Button, Avatar, Card, Upload, Select, message, notification,
} from 'antd';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CHANGE_PASSWORD, GET_ME } from '../../../graphql';
import { uploadFile } from '../../../upload';
import childImg from '../../../assets/images/child.jpg';
import PasswordModal from './PasswordModal';
import { AuthContext } from '../../../appContext/AuthContext';
import { LOGIN } from '../../../routes';
import ViewSpinner from '../../../components/viewSpinner/ViewSpinner';
import './styles.less';

const { Item: FormItem } = Form;
const { Option } = Select;
const StudentProfile = () => {
  const [changePassword, showChangePassword] = useState(false);
  const { revokeAuth } = useContext(AuthContext);
  // `me` rather than a query taking an id — the account is the token's.
  const { data: userPayload, loading: loadingUser, refetch } = useQuery(GET_ME);
  const client = useApolloClient();
  const [uploadLoading, setUploadLoading] = useState(false);
  const [mutateChangePassword, { loading: changePassLoading }] = useMutation(CHANGE_PASSWORD);
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!loadingUser && userPayload) {
      const { me } = userPayload;
      setUser(me);
      setProfile(me.profileStudent?.profileImg);
    }
  }, [userPayload, loadingUser]);
  const handleFileUpload = async ({ file, onSuccess, onError }) => {
    setUploadLoading(true);
    try {
      // `targetId` is ignored for a profile image — the server applies it to
      // the caller.
      const upload = await uploadFile({
        client, file, target: 'PROFILE_IMAGE', targetId: user.id,
      });
      setProfile(upload.url);
      // What is stored is an object path; the readable URL is signed per
      // request, so the record is re-read rather than patched locally.
      await refetch();
      message.success('Profile picture updated successfully');
      onSuccess({ url: upload.url });
    } catch (error) {
      onError(error);
      message.error(error.message || 'Error, could not update picture');
    } finally {
      setUploadLoading(false);
    }
  };

  const getCohortSubjects = (cohortId, allSubjects) => {
    const subjects = allSubjects.filter((subject) => subject.cohortId === cohortId)
      .map(({ name }) => name);
    return subjects;
  };
  const handleFormSubmit = async (inputValue) => {
    const { oldPassword, newPassword } = inputValue;
    try {
      await mutateChangePassword({
        variables: {
          input: {
            newPassword,
            oldPassword,
          },
        },
      });
      notification.success({ message: 'Password successfully changed', description: 'Please login with your new password' });
      revokeAuth();
      navigate(LOGIN);
    } catch (error) {
      message.error('Error, could not change password');
    }
  };
  return (
    <div className="profile-student">
      <PasswordModal
        handleCancel={() => showChangePassword(false)}
        loading={changePassLoading}
        visible={changePassword}
        handleFormSubmit={handleFormSubmit}
      />
      <h4 className="ic-ft-dosis header">My Profile</h4>
      { !loadingUser && user && (
      <>
        <Card className="profile-card" title="Student Information">
          <Row>
            <Col md={10} xs={24}>
              <div className="photo-card">
                <div className="avatar-wrapper">
                  <Avatar src={profile || childImg} size="large" className="student-avatar" />
                </div>
                <Upload
                  customRequest={handleFileUpload}
                  accept="image/png,image/jpeg,image/jpg"
                  showUploadList={false}
                >
                  <Button
                    loading={uploadLoading}
                    className="upload-btn"
                    icon={<UploadOutlined />}
                  >
                    Change Picture
                  </Button>
                </Upload>
              </div>
            </Col>
            <Col md={14} xs={24}>
              <Form layout="vertical">
                <FormItem initialValue={user.firstName} label="First Name" name="firstName">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={user.lastName} label="Last Name" name="lastName">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={user.profileStudent.admissionNumber} label="Admission No" name="adminNo">
                  <Input disabled />
                </FormItem>
                <FormItem initialValue={user.email} label="Email" name="email">
                  <Input disabled />
                </FormItem>
              </Form>
              <Button onClick={() => showChangePassword(true)} className="change-pass-btn">Change Password</Button>
            </Col>
          </Row>
        </Card>
        <Card className="cohort-card" title="Student Cohort and Subjects">
          <Form layout="vertical">
            {user.cohorts.map(({ name, id: cohortId }, idx) => (
              <Row key={cohortId} className="no-flex-column">
                <Col xs={24} sm={12}>
                  <FormItem initialValue={name} name={`cohort_${cohortId}`} label={idx === 0 ? 'Cohort' : ''}>
                    <Input disabled />
                  </FormItem>
                </Col>
                <Col xs={24} sm={12}>
                  <FormItem initialValue={getCohortSubjects(cohortId, user.subjects)} name={`subject_${cohortId}`} label={idx === 0 ? 'Subjects' : ''}>
                    <Select disabled mode="multiple">
                      <Option key="1" value="1">Maths</Option>
                    </Select>
                  </FormItem>
                </Col>
              </Row>
            ))}
          </Form>
        </Card>
      </>
      )}
      {loadingUser && <ViewSpinner />}
    </div>
  );
};

export default StudentProfile;
